import {
  AddOutlined,
  PageDS,
  Spacer,
  TabMenu,
  ToastContainerDS,
} from "@qivia/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../redux/hooks";
import { selectManager } from "../homeSlice";
import "../../../i18n/i18n";
import { SummaryTab } from "./summaryTab";
import { ChargingTrackingTab } from "./chargingTrackingTab";
import { LinkyConnectionsTab } from "./linkyConnectionsTab";
import { HomeChargingDriversModal } from "./modals/homeChargingDriversModal";
import { ElectricVehiclesTab } from "./electricVehiclesTab";
import { ReimburseChargesModal } from "./reimburseChargesModal";

export const ElectricCharging = (props: {
  basePath: "/home" | "/departments";
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const manager = useAppSelector(selectManager);
  const { basePath } = props;

  const selectedTab = params.tab ?? "summary";
  const isOwnerOrAdmin = useMemo(
    () => manager?.role === "OWNER" || manager?.role === "ADMIN",
    [manager],
  );
  const [isHomeChargingDriversModalOpen, setIsHomeChargingDriversModalOpen] =
    useState<boolean>(false);
  const [isReimburseModalOpen, setIsReimburseModalOpen] =
    useState<boolean>(false);

  const tabs = useMemo(
    () => [
      {
        name: "summary",
        title: t("electric_charging.tab.summary"),
      },
      {
        name: "charging_tracking",
        title: t("electric_charging.tab.charging_tracking"),
      },
      {
        name: "electric_vehicles",
        title: t("electric_charging.tab.electric_vehicles"),
      },
      {
        name: "linky_connections",
        title: t("electric_charging.tab.linky_connections"),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (import.meta.env["VITE_TARGET_ENV"] === "production") {
      navigate(basePath);
    }
  }, [navigate, basePath]);

  useEffect(() => {
    if (!params.tab) {
      navigate(`${basePath}/electric_charging/summary`, { replace: true });
    }
  }, [params.tab, basePath, navigate]);

  const canAddHomeChargingDrivers = useMemo(
    () => selectedTab === "linky_connections" && isOwnerOrAdmin,
    [selectedTab, isOwnerOrAdmin],
  );

  const canValidateReimbursements = useMemo(
    () => selectedTab === "charging_tracking" && isOwnerOrAdmin,
    [selectedTab, isOwnerOrAdmin],
  );

  const cta = useMemo(() => {
    if (canValidateReimbursements) {
      return {
        title: t("electric_charging.reimburseModal.open"),
        action: () => {
          setIsReimburseModalOpen(true);
        },
        width: 16,
      };
    }
    if (canAddHomeChargingDrivers) {
      return {
        title: t("linky_consents.cta.addDrivers"),
        action: () => {
          setIsHomeChargingDriversModalOpen(true);
        },
        width: 14,
        leftIcon: <AddOutlined />,
      };
    }
    return undefined;
  }, [canValidateReimbursements, canAddHomeChargingDrivers, t]);

  return (
    <PageDS
      title={t("electric_charging.title")}
      cta={cta}
      toaster={<ToastContainerDS />}
      topElement={
        <>
          <Spacer y={1} />
          <TabMenu
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={(tab) =>
              navigate(`${basePath}/electric_charging/${tab}`)
            }
          />
          <Spacer y={1.5} />
        </>
      }
      supportButton={{
        showMail: true,
      }}
    >
      <SummaryTab />
      <ChargingTrackingTab />
      <ElectricVehiclesTab />
      <LinkyConnectionsTab />
      <HomeChargingDriversModal
        visible={canAddHomeChargingDrivers && isHomeChargingDriversModalOpen}
        onCloseModal={() => {
          setIsHomeChargingDriversModalOpen(false);
        }}
      />
      <ReimburseChargesModal
        isVisible={isReimburseModalOpen}
        onClose={() => setIsReimburseModalOpen(false)}
      />
    </PageDS>
  );
};
